import React, { memo, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Map, List, fromJS } from 'immutable';
import moment from 'moment';
import Helmet from 'react-helmet';

import { MailingInfo } from '../components/MailingInfo';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import { buildSortByParam } from '../helpers/paramBuilder';
import { showError, showInformation } from '../utils/MessagePopup';
import config from '../config';
import * as actionCreators from '../actions/mailingInfo';
import { fetchTargetStatuses } from '../actions/statuses';
import { isResearcher } from '../utils/checkPermissions';
import downloadFile from '../utils/downloadFile';
import uniqueId from '../utils/uniqueId';
import { usePopupContext } from '../contexts/PopupContext';

const values = config.mailingInfo.get('values');
const { func } = PropTypes;

const hasAccess = user => {
  if (!user) return false;

  return !isResearcher(user.getIn(['roles', 0, 'slug'], null));
};

/** Mailing page container. */
const MailingInfoContainer = props => {
  const {
    history,
    user,
    mailingInfo,
    values: mailingInfoValues,
    getMailingHeaderInfo,
    getMailingTargets,
    onFetchStatuses,
    clearUpdateCellError,
    getEmailTemplates,
    updateMailingInfo,
    sendEmailTemplates,
    updateEmailAttachment,
    uploadEmailAttachment,
    updateCompany,
    updatePeople,
    updateContactTitle,
    updateEmail,
    updateMailingStatuses,
    updateMarkCompleted,
    updateSetNextActions,
    cancelMailing,
  } = props;

  const { openPopup, closePopup } = usePopupContext();

  const [statuses, setStatuses] = useState(List());
  const [emailTemplateId, setEmailTemplateId] = useState(null);
  const [batchId] = useState(() => new URLSearchParams(location.search).get('id'));
  const [, forceRender] = useReducer(counter => counter + 1, 0);

  const userHasAccess = hasAccess(user);

  const hasMountedRef = useRef(false);
  const previousMailingInfoRef = useRef(mailingInfo);

  useEffect(() => {
    if (!userHasAccess) {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });

      return;
    }

    getMailingHeaderInfo({
      batchId,
    });
    getMailingTargets({
      batchId,
    });

    onFetchStatuses().then(({ data }) => {
      if (data) {
        setStatuses(fromJS(data));
      }
    });
  }, []);

  const handleDoubleClickRow = useCallback(row => {
    window.open(`/company/${row.data.targetId}/target`, '_blank');
  }, []);

  const handleChange = useCallback(
    data => {
      updateMailingInfo({
        filterName: data.filterName,
        filterData: data.filterData,
      });
    },
    [updateMailingInfo],
  );

  const handleChangeEmailTemplate = useCallback(data => {
    setEmailTemplateId(data.target.value);
  }, []);

  const handleEmailPreviewTemplate = useCallback(() => {
    openPopup('EmailTemplatePreviewPopup', {
      templateId: emailTemplateId,
    });
  }, [openPopup, emailTemplateId]);

  const handleGetNextPageData = useCallback(
    page => {
      if (!batchId) {
        return;
      }

      const sortBy = buildSortByParam(mailingInfo);

      getMailingTargets({
        batchId,
        page,
        sortBy,
      });
    },
    [batchId, mailingInfo, getMailingTargets],
  );

  const handleUpdateEmailAttachment = useCallback(
    (id, fileNames) => {
      updateEmailAttachment({ id, fileNames });
    },
    [updateEmailAttachment],
  );

  const handleReload = useCallback(
    (callback = () => {}) => {
      if (!batchId) {
        return;
      }

      const sortBy = buildSortByParam(mailingInfo);

      getMailingTargets({
        batchId,
        page: 1,
        sortBy,
        afterSuccess: callback,
      });
    },
    [batchId, mailingInfo, getMailingTargets],
  );

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousMailingInfoRef.current = mailingInfo;

      return;
    }

    const previousMailingInfo = previousMailingInfoRef.current;

    previousMailingInfoRef.current = mailingInfo;

    if (previousMailingInfo !== mailingInfo) {
      const oldSortBy = buildSortByParam(previousMailingInfo);
      const sortBy = buildSortByParam(mailingInfo);

      if (sortBy !== oldSortBy) {
        getMailingTargets({
          batchId,
          page: 1,
          sortBy,
        });
      }

      if (mailingInfo.get('header')?.size > 0 && mailingInfo.get('emailTemplates').size === 0) {
        getEmailTemplates({
          activity: mailingInfo.getIn(['header', 'activity']),
          buyerId: mailingInfo.getIn(['header', 'buyerId']),
          projectId: mailingInfo.getIn(['header', 'projectId']),
        });
      }
    }

    const updateCellError = mailingInfo.get('updateCellError');

    if (updateCellError) {
      clearUpdateCellError();
      showError(openPopup, mailingInfo.get('errors'), handleReload);
    }
  }, [mailingInfo, batchId, getMailingTargets, getEmailTemplates, clearUpdateCellError, openPopup, handleReload]);

  const handleUpdateCell = useCallback(
    ({ updateType, updateField, newValue, data }) => {
      if (updateType === 'company') {
        updateCompany(data.targetId, { [updateField]: newValue }, () => {
          handleReload();
        });
      } else if (updateType === 'people') {
        updatePeople(data.contactId, { [updateField]: newValue }, () => {
          handleReload();
        });
      } else if (updateType === 'contactTitle') {
        updateContactTitle(data.contactId, data.entityContactsId, { [updateField]: newValue }, () => {
          handleReload();
        });
      } else if (updateType === 'email') {
        updateEmail(data.contactId, data.contactEmailId, { [updateField]: newValue }, () => {
          handleReload();
        });
      }
    },
    [updateCompany, updatePeople, updateContactTitle, updateEmail, handleReload],
  );

  const handleUpdateMarkCompleted = useCallback(
    (formData, callback) => {
      if (!batchId) {
        return;
      }

      const statusId = statuses.findKey(
        item => item.get('fc') === formData.getIn(['status', 'selected', 'value'], undefined),
      );

      updateMarkCompleted({
        batchId,
        body: {
          date: formData.get('date', moment()).format('YYYY-MM-DD'),
          status_id: statusId,
        },
        callback,
      });
    },
    [batchId, statuses, updateMarkCompleted],
  );

  const handleUpdateSetNextActions = useCallback(
    (formData, callback) => {
      if (!batchId) {
        return;
      }

      const selectedStatus = statuses
        .filter(item => item.get('fc') === formData.getIn(['status', 'selected', 'value'], Map()))
        .get(0);
      const statusId = statuses.indexOf(selectedStatus);
      const params = {
        override: formData.get('override'),
        backlogged: formData.get('backlog', false),
      };

      if (statusId >= 0) {
        params.status_id = statusId;
      }

      if (formData.get('date')) {
        params.date = formData.get('date').format('YYYY-MM-DD');
      }

      if (formData.getIn(['assignedTo', 'selected'])) {
        params.user_id = formData.getIn(['assignedTo', 'selected', 'id']);
      }

      if (formData.getIn(['activity', 'selected'])) {
        params.activity = formData.getIn(['activity', 'selected', 'value']);
      }

      if (formData.get('description')) {
        params.description = formData.get('description');
      }

      updateSetNextActions({
        batchId,
        body: {
          params,
        },
        callback,
      });
    },
    [batchId, statuses, updateSetNextActions],
  );

  const handleSetNextActions = useCallback(() => {
    const analystName = mailingInfo.getIn(['header', 'recordSubOwnerUserName']) || '';
    const analystId = mailingInfo.getIn(['header', 'recordSubOwnerId']);

    if (!batchId) {
      return;
    }

    openPopup('SetNextActionsPopup', {
      currentPage: 'browse',
      onUpdate: handleUpdateSetNextActions,
      onSuccessResponse: ({ errors }) => {
        if (errors && errors.length > 0) {
          const eventIds = errors.map(({ eventId }) => eventId);
          let message = '';

          message = errors.reduce((accumulator, error) => {
            accumulator += `${error.error}\n`;

            return accumulator;
          }, '');

          openPopup('ConfirmPopup', {
            message,
            header: 'The state of the list has been changed',
            submessage: 'Click “Update” to remove these targets from the list',
            yes: 'Update',
            no: 'Cancel',
            danger: false,
            onOk: () =>
              updateMailingStatuses({
                batchId,
                body: {
                  selected_ids: eventIds,
                },
                callback: () => {
                  handleReload();
                  closePopup();
                },
              }),
            onCancel: () => closePopup(),
          });
        } else {
          handleReload();
        }
      },
      refreshGrid: () => {
        forceRender();
      },
      assignedTo: {
        suggestions: [],
        text: analystName,
        selected: { text: analystName, id: analystId },
      },
    });
  }, [mailingInfo, batchId, openPopup, closePopup, updateMailingStatuses, handleReload, handleUpdateSetNextActions]);

  const handleMarkCompleted = useCallback(() => {
    if (!batchId) {
      return;
    }

    openPopup('MarkMailingCompletedPopup', {
      onUpdate: handleUpdateMarkCompleted,
      updateListStatuses: eventIds =>
        updateMailingStatuses({
          batchId,
          body: {
            selected_ids: eventIds,
          },
          callback: () => {
            handleReload();
          },
        }),
      refreshGrid: () => {
        const callback = () => {
          getMailingHeaderInfo({
            batchId,
            afterSuccess: handleSetNextActions,
          });
        };

        handleReload(callback);
      },
    });
  }, [
    batchId,
    openPopup,
    updateMailingStatuses,
    handleReload,
    handleUpdateMarkCompleted,
    getMailingHeaderInfo,
    handleSetNextActions,
  ]);

  const handleSendEmailTemplates = useCallback(
    (selected, afterSuccess) => {
      const selectedMailingInfo = mailingInfo
        .get('queryResults')
        .reduce((accumulator, queryResult) => {
          accumulator.push({ targetId: queryResult.targetId, attachmentIds: [] });

          return accumulator;
        }, [])
        .map(queryResult => {
          const foundWithAttachment = selected?.find(item => item.targetId === queryResult.targetId);

          if (foundWithAttachment) {
            return foundWithAttachment;
          }

          return queryResult;
        });

      const selectedMailingInfoLength = selectedMailingInfo.length;

      sendEmailTemplates({
        body: {
          selected: selectedMailingInfo,
          templateId: emailTemplateId,
          batchId,
          is_fake_sending: window.location.host.startsWith('localhost'),
        },
        afterSuccess: ({ response }) => {
          afterSuccess();
          closePopup();

          mailingInfo.get('queryResults').forEach(queryResult => {
            updateEmailAttachment({ id: queryResult.id, fileNames: null });
          });

          const skipCLength = response.data.skipC.length;
          const skipELength = response.data.skipE.length;
          const skipPCLength = response.data.skipPC.length;
          const skipPCELength = response.data.skipPCE.length;

          const successfullySentCount =
            selectedMailingInfoLength - skipPCLength - skipPCELength - skipELength - skipCLength;

          if (successfullySentCount !== selectedMailingInfoLength) {
            const popupMessages = [];

            popupMessages.push(`${successfullySentCount}/${selectedMailingInfoLength} emails were successfully sent.`);

            if (skipPCELength > 0) {
              let message = '';
              const missingTitle = `${skipPCELength}/${selectedMailingInfoLength} emails were not sent because the Primary Contact's email address was missing. \n`;

              response.data.skipPCE.forEach(error => {
                const link = `(${window.location.origin}/company/${error.companyId})`;
                const companyName = `${error.companyName} ${link}`;

                message = `${message} ${companyName} \n`;
              });
              popupMessages.push(`${missingTitle} ${message}`);
            }

            if (skipPCLength > 0) {
              let message = '';
              const missingTitle = `${skipPCLength}/${selectedMailingInfoLength} emails were not sent due to missing Primary Contact. \n`;

              response.data.skipPC.forEach(error => {
                const link = `(${window.location.origin}/company/${error.companyId})`;
                const companyName = `${error.companyName} ${link}`;

                message = `${message} ${companyName} \n`;
              });
              popupMessages.push(`${missingTitle} ${message}`);
            }

            if (skipELength > 0) {
              let message = '';
              const missingTitle = `${skipELength}/${selectedMailingInfoLength} emails were not sent because we have problem with Sendgrid Service. \n`;

              response.data.skipE.forEach(error => {
                const link = `(${window.location.origin}/company/${error.companyId})`;
                const companyName = `${error.companyName} ${link}`;

                message = `${message} ${companyName} \n`;
              });
              popupMessages.push(`${missingTitle} ${message}`);
            }

            if (skipCLength > 0) {
              let message = '';
              const missingTitle = `${skipCLength}/${selectedMailingInfoLength} emails were not sent because there are records with a company that was not found. \n`;

              response.data.skipC.forEach(error => {
                const link = `(${window.location.origin}/company/${error.companyId})`;
                const companyName = `${error.companyName} ${link}`;

                message = `${message} ${companyName} \n`;
              });
              popupMessages.push(`${missingTitle} ${message}`);
            }

            showInformation(openPopup, popupMessages);
          }

          if (skipPCLength + skipPCELength + skipELength + skipCLength === 0) {
            const toBe = selectedMailingInfoLength === 1 ? 'email was' : 'emails were';

            showInformation(openPopup, [
              `${selectedMailingInfoLength}/${selectedMailingInfoLength} ${toBe} successfully sent.`,
            ]);
          }
        },
        afterError: ({ responseBody }) => {
          showError(openPopup, [responseBody?.message || 'An error occurred']);
        },
      });
    },
    [mailingInfo, sendEmailTemplates, updateEmailAttachment, emailTemplateId, batchId, closePopup, openPopup],
  );

  const handleSendEmails = useCallback(
    (files, afterSuccess) => {
      openPopup('MailingInfoSendPopup', {
        uploadEmailAttachment,
        files,
        mailingInfo,
        onOk: selected => {
          handleSendEmailTemplates(selected, afterSuccess);
        },
        onCancel: () => closePopup(),
      });
    },
    [openPopup, closePopup, uploadEmailAttachment, mailingInfo, handleSendEmailTemplates],
  );

  const handleDownloadMergeSource = useCallback(() => {
    openPopup('DownloadMergeSourcePopup', {
      onDownload: params => {
        if (!batchId) {
          return;
        }

        const url = `${config.API_BASE_URL}/api/v1/events/excel_merge_source/export/`;

        downloadFile({
          url,
          params: {
            batch_id: batchId,
            file_format: params.format,
            blank_fields: params.blankFields,
            is_save_default: params.isSaveDefault,
            // Unique value prevents the browser from serving a cached file with outdated Target fields.
            hash: uniqueId(),
          },
        });
      },
    });
  }, [openPopup, batchId]);

  const handleQuickMergeSource = useCallback(() => {
    if (!batchId) {
      return;
    }

    const url = `${config.API_BASE_URL}/api/v1/events/excel_merge_source/export/`;

    downloadFile({
      url,
      params: {
        batch_id: batchId,
        hash: uniqueId(),
      },
    });
  }, [batchId]);

  const handleMailingLabelsMerge = useCallback(() => {
    if (!batchId) {
      return;
    }

    const url = `${config.API_BASE_URL}/api/v1/windward/merge_source/`;

    downloadFile({
      url,
      params: {
        batch_id: batchId,
        hash: uniqueId(),
      },
    });
  }, [batchId]);

  const handleProjectTemplate = useCallback(() => {
    if (!batchId) {
      return;
    }

    const url = `${config.API_BASE_URL}/api/v1/batch/mailing/mailing_template/${batchId}`;

    downloadFile({ url });
  }, [batchId]);

  const handleDownloadLogos = useCallback(() => {
    if (!batchId) {
      return;
    }

    const url = `${config.API_BASE_URL}/api/v1/batch/${batchId}/logos`;

    const handleResponseJSON = data => {
      showError(openPopup, [data.message]);
    };

    downloadFile({ url, onResponseJSON: handleResponseJSON });
  }, [batchId, openPopup]);

  const handleCancelMailing = useCallback(() => {
    const targetsNumber = mailingInfo.get('queryResults').size;

    if (!batchId) {
      return;
    }

    openPopup('ConfirmPopup', {
      message: `Do you wish to cancel this mailing and return all ${targetsNumber} targets to the Backlog?`,
      header: 'Cancel Mailing',
      yes: 'Yes',
      no: 'No',
      onOk: () =>
        cancelMailing({
          batchId,
          afterSuccess: () => {
            closePopup();
            history.goBack();
          },
        }),
      onCancel: () => closePopup(),
    });
  }, [mailingInfo, batchId, openPopup, closePopup, cancelMailing, history]);

  if (!userHasAccess) return null;

  return (
    <div className="full-height">
      <Helmet title="Mailing Page" />
      <MailingInfo
        emailTemplateId={emailTemplateId}
        mailingInfo={mailingInfo}
        onCancelMailing={handleCancelMailing}
        onChange={handleChange}
        onChangeEmailTemplate={handleChangeEmailTemplate}
        onDoubleClickRow={handleDoubleClickRow}
        onDownloadLogos={handleDownloadLogos}
        onDownloadMergeSource={handleDownloadMergeSource}
        onGetNextPageData={handleGetNextPageData}
        onMailingLabelsMerge={handleMailingLabelsMerge}
        onMarkCompleted={handleMarkCompleted}
        onPreviewEmailTemplate={handleEmailPreviewTemplate}
        onProjectTemplate={handleProjectTemplate}
        onQuickMergeSource={handleQuickMergeSource}
        onReload={handleReload}
        onSendEmails={handleSendEmails}
        onSetNextActions={handleSetNextActions}
        onUpdateCell={handleUpdateCell}
        onUpdateEmailAttachment={handleUpdateEmailAttachment}
        values={mailingInfoValues}
      />
    </div>
  );
};

MailingInfoContainer.propTypes = {
  clearUpdateCellError: func.isRequired,
  getMailingHeaderInfo: func.isRequired,
  getMailingTargets: func.isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  mailingInfo: PropTypes.instanceOf(Map).isRequired,
  updateCompany: func.isRequired,
  updateContactTitle: func.isRequired,
  updateEmail: func.isRequired,
  updateEmailAttachment: func.isRequired,
  updateMailingInfo: func.isRequired,
  updateMailingStatuses: func.isRequired,
  updateMarkCompleted: func.isRequired,
  updatePeople: func.isRequired,
};

const mapStateToProps = state => ({
  mailingInfo: state.mailingInfo,
  user: state.auth.get('user', null),
  values,
});

const mapDispatchToProps = dispatch => ({
  onFetchStatuses: () => dispatch(fetchTargetStatuses()),
  ...bindActionCreators(actionCreators, dispatch),
});

export { MailingInfoContainer };

export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(MailingInfoContainer));
